import { z } from 'zod';
import {
  defaultMarketplaceSearchSelection,
  normalizeSelectedPaths,
} from './search';

export const filterTypeZod = z.enum([
  'text',
  'choice',
  'domain',
  'type',
  'boolean',
  'date',
]);
export type FilterType = z.infer<typeof filterTypeZod>;

export const filterOperatorZod = z.enum([
  'equals',
  'startsWith',
  'endsWith',
  'contains',
  'in',
  'notIn',
  'inIgnoreCase',
  'notInIgnoreCase',
  'onOrAfter',
  'onOrBefore',
]);
export type FilterOperator = z.infer<typeof filterOperatorZod>;

export const filterDefinitionZod = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: filterTypeZod,
    operators: z.array(filterOperatorZod).min(1).min(1),
    facetable: z.boolean(),
    defaultValues: z.array(z.string()).optional(),
    applicableDataLandscapes: z.array(z.string().min(1)).optional(),
  })
  .strict();
export type FilterDefinition = z.infer<typeof filterDefinitionZod>;

export const filterClauseZod = z
  .object({
    id: z.string().min(1),
    operator: filterOperatorZod,
    values: z.array(z.union([z.string(), z.boolean()])),
  })
  .strict();
export type FilterClause = z.infer<typeof filterClauseZod>;

const boundedFiltersZod = z.array(filterClauseZod).max(20);

export const productMapSearchRequestZod = z
  .object({
    environment: z.string().min(1),
    query: z.string().trim().min(1).max(100),
    filters: boundedFiltersZod.default([]),
    pageLimit: z.number().int().positive().default(25),
    cursor: z.string().min(1).optional(),
  })
  .strict();

export type ProductMapSearchRequest = z.input<
  typeof productMapSearchRequestZod
>;
export type ProductMapSearchResponse = {
  results: Array<{ urn: string; score: number }>;
  nextPageCursor?: string;
};

export const sortDirectionZod = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof sortDirectionZod>;

export const sortDirectionsZod = z
  .object({
    asc: z.string().min(1).optional(),
    desc: z.string().min(1).optional(),
  })
  .strict()
  .refine(value => value.asc !== undefined || value.desc !== undefined);

export const sortDefinitionZod = z
  .object({
    id: z.string().min(1),
    directions: sortDirectionsZod,
  })
  .strict();
export type SortDefinition = z.infer<typeof sortDefinitionZod>;

export const sortSelectionZod = z
  .object({
    id: z.string().min(1),
    direction: sortDirectionZod,
  })
  .strict();

export const facetRequestZod = z
  .object({
    mode: z.enum(['keyword', 'semantic']),
    environment: z.string().min(1),
    query: z.string().max(100),
    filters: boundedFiltersZod,
    facetId: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(500).optional(),
    cursor: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((request, ctx) => {
    if (request.mode === 'semantic' && !request.query.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['query'],
        message: 'Semantic facets require a non-empty query',
      });
    }
    if (request.cursor && !request.facetId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cursor'],
        message: 'A facet cursor requires facetId',
      });
    }
  });

const searchRequestShape = {
  mode: z.enum(['keyword', 'semantic']),
  environment: z.string().min(1),
  query: z.string().trim().max(100),
  filters: boundedFiltersZod.default([]),
  sort: sortSelectionZod.optional(),
  cursor: z.string().min(1).optional(),
};

export const summarySearchRequestZod = z
  .object({
    ...searchRequestShape,
    category: z.literal('summary'),
    limit: z.number().int().min(1).max(50).default(25),
  })
  .strict();

export const completeSearchRequestZod = z
  .object({
    ...searchRequestShape,
    category: z.literal('complete'),
    limit: z.number().int().min(1).max(25).default(25),
  })
  .strict();

export const selectedSearchRequestZod = z
  .object({
    ...searchRequestShape,
    category: z.literal('selected'),
    limit: z.number().int().min(1).max(1000).default(25),
    useHighlight: z.boolean().default(false),
    select: z
      .array(z.string())
      .optional()
      .transform(paths =>
        paths === undefined
          ? [...defaultMarketplaceSearchSelection]
          : normalizeSelectedPaths(paths),
      ),
  })
  .strict();

export const searchRequestZod = z
  .discriminatedUnion('category', [
    summarySearchRequestZod,
    completeSearchRequestZod,
    selectedSearchRequestZod,
  ])
  .superRefine((request, ctx) => {
    if (
      request.category === 'selected' &&
      request.mode === 'semantic' &&
      request.limit > 50
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: 50,
        type: 'number',
        inclusive: true,
        path: ['limit'],
        message: 'Semantic selected searches support at most 50 items per page',
      });
    }
  });
export type MarketplaceSearchRequest = z.infer<typeof searchRequestZod>;
export type MarketplaceSearchRequestInput = z.input<typeof searchRequestZod>;

export const searchTotalsZod = z
  .object({
    items: z.number().int().min(0),
  })
  .strict();
export type SearchTotals = z.infer<typeof searchTotalsZod>;

export const highlightRangeZod = z
  .object({
    start: z.number().int().min(0),
    end: z.number().int().min(1),
  })
  .strict()
  .refine(range => range.start < range.end, {
    message: 'Highlight ranges must be non-empty',
  });
export type HighlightRange = z.infer<typeof highlightRangeZod>;

export const highlightFragmentZod = z
  .object({
    text: z.string(),
    ranges: z.array(highlightRangeZod).min(1),
  })
  .strict()
  .superRefine((fragment, ctx) => {
    const codePointLength = Array.from(fragment.text).length;
    let previousEnd = 0;
    fragment.ranges.forEach((range, index) => {
      if (range.start < previousEnd || range.end > codePointLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ranges', index],
          message: 'Highlight ranges must be ordered, disjoint, and in bounds',
        });
      }
      previousEnd = range.end;
    });
  });
export type HighlightFragment = z.infer<typeof highlightFragmentZod>;

export const searchHighlightZod = z
  .object({
    field: z.string().min(1),
    fragments: z.array(highlightFragmentZod).min(1),
  })
  .strict();
export type SearchHighlight = z.infer<typeof searchHighlightZod>;

const highlightsZod = z.array(searchHighlightZod).min(1);

const namedReferenceZod = z
  .object({
    urn: z.string().min(1),
    name: z.string().min(1),
  })
  .strict();

export const summarySearchResultZod = z
  .object({
    kind: z.enum(['product', 'consumable']),
    urn: z.string().min(1),
    marketplaceUrl: z.string().url().optional(),
    environment: z.string().min(1),
    name: z.string().min(1),
    description: z.string().optional(),
    type: z.string().min(1),
    typeDisplayName: z.string().min(1).optional(),
    version: z.string().min(1),
    publishedAt: z.string().datetime({ offset: true }),
    domain: namedReferenceZod,
    owner: z
      .object({
        ref: z.string().min(1),
        displayName: z.string().min(1),
      })
      .strict(),
    product: z
      .object({
        urn: z.string().min(1),
        displayName: z.string().min(1),
        marketplaceUrl: z.string().url().optional(),
      })
      .strict()
      .optional(),
    rank: z.number().finite().optional(),
  })
  .strict()
  .superRefine((result, ctx) => {
    if (result.kind === 'product' && result.product !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['product'],
        message: 'Parent product information is only valid for consumables',
      });
    }
  });
export type SummarySearchResult = z.infer<typeof summarySearchResultZod>;

export const completeSearchResultZod = z
  .object({})
  .passthrough()
  .superRefine((result, ctx) => {
    for (const reservedField of ['descriptor', '_expanded']) {
      if (reservedField in result) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [reservedField],
          message: `${reservedField} is not part of a complete search result`,
        });
      }
    }
  });
export type CompleteSearchResult = z.infer<typeof completeSearchResultZod>;

export const selectedSearchResultZod = z
  .object({
    type: z.string().optional(),
    external_id: z.string().optional(),
    document: z.object({}).passthrough().optional(),
    highlights: highlightsZod.optional(),
  })
  .strict();
export type SelectedSearchResult = z.infer<typeof selectedSearchResultZod>;

function validateCursorPresence(
  response: { hasMore: boolean; nextCursor?: string },
  ctx: z.RefinementCtx,
): void {
  if (response.hasMore !== (response.nextCursor !== undefined)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['nextCursor'],
      message: 'nextCursor must be present exactly when hasMore is true',
    });
  }
}

export const summarySearchResponseZod = z
  .object({
    totals: searchTotalsZod,
    items: z.array(summarySearchResultZod),
    hasMore: z.boolean(),
    nextCursor: z.string().min(1).optional(),
  })
  .strict()
  .superRefine(validateCursorPresence);
export type SummarySearchResponse = z.infer<typeof summarySearchResponseZod>;

export const completeSearchResponseZod = z
  .object({
    totals: searchTotalsZod,
    items: z.array(completeSearchResultZod),
    hasMore: z.boolean(),
    nextCursor: z.string().min(1).optional(),
  })
  .strict()
  .superRefine(validateCursorPresence);
export type CompleteSearchResponse = z.infer<typeof completeSearchResponseZod>;

export const selectedSearchResponseZod = z
  .object({
    totals: searchTotalsZod,
    items: z.array(selectedSearchResultZod),
    hasMore: z.boolean(),
    nextCursor: z.string().min(1).optional(),
  })
  .strict()
  .superRefine(validateCursorPresence);
export type SelectedSearchResponse = z.infer<typeof selectedSearchResponseZod>;

export const searchResponseZod = z.union([
  summarySearchResponseZod,
  completeSearchResponseZod,
  selectedSearchResponseZod,
]);
export type MarketplaceSearchResponse = z.infer<typeof searchResponseZod>;

export type MarketplaceSearchResponseByCategory = {
  summary: SummarySearchResponse;
  complete: CompleteSearchResponse;
  selected: SelectedSearchResponse;
};

export enum SearchErrorCode {
  invalid_request = 'invalid_request',
  invalid_environment = 'invalid_environment',
  invalid_filter = 'invalid_filter',
  invalid_sort = 'invalid_sort',
  invalid_cursor = 'invalid_cursor',
  unauthenticated = 'unauthenticated',
  forbidden = 'forbidden',
  unsupported_mode = 'unsupported_mode',
  rate_limited = 'rate_limited',
  search_unavailable = 'search_unavailable',
  semantic_disabled = 'semantic_disabled',
  semantic_not_ready = 'semantic_not_ready',
  internal_error = 'internal_error',
}

export const searchErrorZod = z
  .object({
    code: z.nativeEnum(SearchErrorCode),
    message: z.string().min(1),
    details: z.record(z.unknown()).nullable(),
    requestId: z.string().min(1),
  })
  .strict();

export const searchErrorResponseZod = z
  .object({ error: searchErrorZod })
  .strict();

export const facetValueZod = z
  .object({
    value: z.string().min(1),
    displayValue: z.string().min(1),
    count: z.number().int().min(0),
  })
  .strict();
export type FacetValue = z.infer<typeof facetValueZod>;

export const facetZod = z
  .object({
    id: z.string().min(1),
    values: z.array(facetValueZod),
    hasMore: z.boolean(),
    nextCursor: z.string().min(1).optional(),
  })
  .strict();
export type Facet = z.infer<typeof facetZod>;

export const facetResponseZod = z
  .object({
    facets: z.array(facetZod),
  })
  .strict();
export type FacetResponse = z.infer<typeof facetResponseZod>;

/**
 * Inferred request type for convenience; use facetRequestZod for validation
 */
export type FacetRequest = z.infer<typeof facetRequestZod>;
